import { HubConnectionState } from '@microsoft/signalr';
import { action, computed, reaction, makeObservable } from 'mobx';
import { homeworkJoinedState } from '../HomeworkJoinedState';
import { Disposers, logger, toastrHelper } from '../../../lib/helpers';
import { HomeworkHubMessage, HomeworkRole } from '../homework-models';
import { HomeworkTasks } from './homework-tasks';
import { HomeworkJoinStore } from './HomeworkJoinStore';
import { HomeworkKeyValuesStore } from './HomeworkKeyValuesStore';
import { HomeworkStateStore } from './HomeworkStateStore';
import { HomeworkTaskStore } from './HomeworkTaskStore';
import { HubConnectionStore } from './HubConnectionStore';
import swal from 'sweetalert2';

export class HomeworkHubStore {
    readonly connection: HubConnectionStore;
    readonly join: HomeworkJoinStore;
    readonly state: HomeworkStateStore;
    readonly keyValues: HomeworkKeyValuesStore;
    readonly tasks: HomeworkTaskStore;

    private readonly disposers = new Disposers();

    constructor(readonly homeworkId: number,
        readonly role: HomeworkRole) {
        makeObservable(this);
        this.connection = new HubConnectionStore();
        this.disposers.add(this.connection);
        this.join = new HomeworkJoinStore(homeworkId, role);
        this.state = new HomeworkStateStore();
        this.keyValues = new HomeworkKeyValuesStore();
        this.tasks = new HomeworkTaskStore();
        this.connection.connection.on('message', this.onReceivedMessage);
        this.setup();
    }


    @action
    start() {
        this.connection.start();
    }

    @computed
    get version(): number | null {
        const homeworkState = this.state.homeworkState;
        return homeworkState === null
            ? null
            : homeworkState.Version;
    }

    @computed
    get lessonSnapshotId(): number | null {
        const homeworkState = this.state.homeworkState;
        return homeworkState === null
            ? null
            : homeworkState.LessonSnapshotId;
    }

    @computed
    get currentPageId(): number | null {
        const homeworkState = this.state.homeworkState;
        return homeworkState === null
            ? null
            : homeworkState.CurrentPageId;
    }

    @computed
    get currentPushableBlockId(): number | null {
        const homeworkState = this.state.homeworkState;
        return homeworkState === null
            ? null
            : homeworkState.CurrentPushableBlockId;
    }

    @computed
    get revealedBlockIds(): ReadonlySet<number> {
        return this.keyValues.revealedBlockIds;
    }

    @computed
    get homeworkBlockIds(): ReadonlySet<number> {
        return this.keyValues.homeworkBlockIds;
    }

    private setup() {
        homeworkJoinedState.setProvider(this.join);
        this.disposers.add(() => homeworkJoinedState.setProvider(null));

        this.disposers.add(
            reaction(
                () => this.connection.connectionState,
                this.onConnectionStateChange,
                { fireImmediately: true }),
            reaction(
                () => this.join.state?.value,
                this.onJoinStateChange
            ),
            reaction(
                () => this.state.homeworkState,
                this.onHomeworkStateChange
            ),
            reaction(
                () => [this.tasks.any, this.tasks.active, this.tasks.state?.value],
                this.onTasksStateChange
            ),
            reaction(
                () => this.keyValues.allChanges,
                this.onKeyValueUpdates
            ),
            reaction(
                () => this.keyValues.missingChangeStamps.size,
                this.onMissingChangeStamps
            ));
        // TODO: reactions
    }

    @action
    private onConnectionStateChange = () => {
        logger.log('HomeworkHubStore.onConnectionStateChange');
        const state = this.connection.connectionState;
        if (state === HubConnectionState.Connected) {
            this.join.join(this.connection.connection);
        }
        else {
            this.join.reset();
            this.state.clearHomeworkState();
            this.keyValues.clear();
            this.tasks.clearAll();
        }
    };

    @action
    private onJoinStateChange = () => {
        const state = this.join.state;
        if (state !== null && state.isResolved)
            this.onReceivedMessage(state.value);
        if (this.join.joined)
            this.tasks.activate();
        else
            this.tasks.deactivate();
    };

    @action
    private onHomeworkStateChange = () => {
        let state = this.state.homeworkState;
        if (state === null) {
            logger.log('onHomeworkStateChange clearing state');
            this.keyValues.clear();
        }
    };

    @action
    private onTasksStateChange = () => {
        if (!this.tasks.active)
            return;
        const state = this.tasks.state;

        if (state !== null) {
            if (state.isResolved) {
                this.onReceivedMessage(state.value);
            }
            if (state.isRejected) {
                this.onError(state.error);
            }
        }

        if (!this.tasks.active)
            return;
        this.tasks.runNext(this.connection.connection);
    };

    @action
    private onKeyValueUpdates = () => {
        if (!this.tasks.active || this.keyValues.changes.size === 0)
            return;
        this.tasks.add(HomeworkTasks.updateKeyValues(this.homeworkId, this.keyValues.allChanges));
    };

    @action
    private onMissingChangeStamps = () => {
        if (!this.tasks.active || this.keyValues.missingChangeStamps.size === 0)
            return;
        const missingChangeStamps = Array.from(this.keyValues.missingChangeStamps);
        this.tasks.add(HomeworkTasks.getKeyValuesPart(this.homeworkId, missingChangeStamps));
    };

    @action
    private onReceivedMessage = async (message: HomeworkHubMessage) => {
        // logger.log('HomeworkHubStore.onReceivedMessage', message);

        switch (message.MessageType) {
            case 'bad-request':
                toastrHelper.error(message.Error);
                break;
            case 'disconnect':
                this.connection.dispose();
                this.connection.dontReconnectMessage = message.Error;
                break;
            case 'reset-connection':
                this.connection.reconnect();
                break;
            case 'homework-joined':
                this.state.updateHomeworkState(message.HomeworkState);
                this.keyValues.mergeComplete(message.KeyValues);
                break;
            case 'homework-state':
                this.state.updateHomeworkState(message.HomeworkState);
                break;
            case 'homework-key-values-updated':
                this.state.updateHomeworkState(message.HomeworkState);
                this.keyValues.mergePartial(message.ChangeStamps, message.Changes);
                break;
            case 'homework-key-values-part':
                this.keyValues.mergePartial(message.ChangeStamps, message.KeyValues);
                break;
            case 'debug':
                logger.log('payload', message.Payload);
                break;
            case 'homework-content-updated':
                await swal.fire({
                    html: 'Homework content was updated.<br> The page will reload in 5 seconds',
                    timer: 5000,
                    didOpen: () => swal.showLoading(),
                    showCancelButton: false,
                    showCloseButton: false,
                    showConfirmButton: false,
                });
                window.location.reload();
                break;
            default:
                break;
        }
    };

    @action
    private onError(error?: any | undefined) {
        logger.log('HomeworkHubStore.onError', error);
    }

    dispose = () => {
        this.disposers.dispose();
    };
}
